export interface RateLimiterOptions {
  windowMs: number;
  maxRequests: number;
  keyGenerator?: (context: any) => string;
  skipSuccessfulRequests?: boolean;
  skipFailedRequests?: boolean;
  onLimitReached?: (identifier: string, info: RateLimitInfo) => void;
}

export interface RateLimitInfo {
  limit: number;
  current: number;
  remaining: number;
  resetTime: number;
  retryAfter: number;
}

interface RequestRecord {
  timestamps: number[];
  blockedUntil?: number;
}

export class RateLimiter {
  private requests: Map<string, RequestRecord> = new Map();
  private cleanupInterval?: NodeJS.Timeout;

  constructor(private options: RateLimiterOptions) {
    // Start cleanup interval to remove old entries
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, Math.min(options.windowMs, 60000)); // Cleanup at least every minute
  }

  /**
   * Check if a request is allowed
   */
  isAllowed(identifier: string, context?: any): boolean {
    const key = this.options.keyGenerator ? 
      this.options.keyGenerator(context) : identifier;
    
    const now = Date.now();
    const record = this.requests.get(key) || { timestamps: [] };

    // Check if temporarily blocked
    if (record.blockedUntil && now < record.blockedUntil) {
      return false;
    }

    // Remove expired timestamps
    record.timestamps = record.timestamps.filter(
      timestamp => now - timestamp < this.options.windowMs
    );

    // Check rate limit
    if (record.timestamps.length >= this.options.maxRequests) {
      const oldestTimestamp = record.timestamps[0];
      const resetTime = oldestTimestamp + this.options.windowMs;
      const retryAfter = resetTime - now;

      // Temporary block for repeated violations
      if (record.timestamps.length > this.options.maxRequests * 1.5) {
        record.blockedUntil = now + this.options.windowMs * 2;
      }

      // Call limit reached callback
      if (this.options.onLimitReached) {
        const info: RateLimitInfo = {
          limit: this.options.maxRequests,
          current: record.timestamps.length,
          remaining: 0,
          resetTime,
          retryAfter: Math.ceil(retryAfter / 1000),
        };
        this.options.onLimitReached(key, info);
      }

      return false;
    }

    // Add current request
    record.timestamps.push(now);
    this.requests.set(key, record);

    return true;
  }

  /**
   * Get current rate limit info for an identifier
   */
  getInfo(identifier: string): RateLimitInfo {
    const now = Date.now();
    const record = this.requests.get(identifier) || { timestamps: [] };
    
    // Remove expired timestamps
    const validTimestamps = record.timestamps.filter(
      timestamp => now - timestamp < this.options.windowMs
    );

    const current = validTimestamps.length;
    const remaining = Math.max(0, this.options.maxRequests - current);
    
    let resetTime = now + this.options.windowMs;
    let retryAfter = 0;

    if (validTimestamps.length > 0) {
      const oldestTimestamp = validTimestamps[0];
      resetTime = oldestTimestamp + this.options.windowMs;
      
      if (current >= this.options.maxRequests) {
        retryAfter = Math.ceil((resetTime - now) / 1000);
      }
    }

    return {
      limit: this.options.maxRequests,
      current,
      remaining,
      resetTime,
      retryAfter,
    };
  }

  /**
   * Reset rate limit for an identifier
   */
  reset(identifier: string): void {
    this.requests.delete(identifier);
  }

  /**
   * Reset all rate limits
   */
  resetAll(): void {
    this.requests.clear();
  }

  /**
   * Record a request result (for conditional limiting)
   */
  recordResult(identifier: string, success: boolean): void {
    if (success && this.options.skipSuccessfulRequests) {
      // Remove the last timestamp if we should skip successful requests
      const record = this.requests.get(identifier);
      if (record && record.timestamps.length > 0) {
        record.timestamps.pop();
      }
    } else if (!success && this.options.skipFailedRequests) {
      // Remove the last timestamp if we should skip failed requests
      const record = this.requests.get(identifier);
      if (record && record.timestamps.length > 0) {
        record.timestamps.pop();
      }
    }
  }

  /**
   * Clean up old entries
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, record] of this.requests.entries()) {
      // Remove expired timestamps
      record.timestamps = record.timestamps.filter(
        timestamp => now - timestamp < this.options.windowMs
      );

      // Remove entries with no timestamps and no active block
      if (record.timestamps.length === 0 && 
          (!record.blockedUntil || now >= record.blockedUntil)) {
        expiredKeys.push(key);
      }
    }

    // Delete expired entries
    expiredKeys.forEach(key => this.requests.delete(key));
  }

  /**
   * Get statistics about current rate limiting
   */
  getStats(): {
    totalIdentifiers: number;
    blockedIdentifiers: number;
    totalRequests: number;
  } {
    const now = Date.now();
    let blockedCount = 0;
    let totalRequests = 0;

    for (const record of this.requests.values()) {
      const validTimestamps = record.timestamps.filter(
        timestamp => now - timestamp < this.options.windowMs
      );
      
      totalRequests += validTimestamps.length;
      
      if (validTimestamps.length >= this.options.maxRequests ||
          (record.blockedUntil && now < record.blockedUntil)) {
        blockedCount++;
      }
    }

    return {
      totalIdentifiers: this.requests.size,
      blockedIdentifiers: blockedCount,
      totalRequests,
    };
  }

  /**
   * Destroy the rate limiter and clean up resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.requests.clear();
  }
}